// GroundingLens — the provenance lens: what a material is grounded on.
//
// For a selected material, one row per element with its honest grounding state
// (grounded / weak / ungrounded) — icon + label + tint, colour never alone, NO
// numeric confidence anywhere (view-agent-inspection.md §5). The verbatim
// source quote IS the proof, revealed by the shared GroundingEvidence panel
// (which also handles the "source moved" degradation — a stored quote with a
// quiet note, never a dead link). A coverage summary ("6 grounded · 2 weak · 1
// unlinked") lets the author see where grounding is thin without opening each
// item. Ungrounded is a calm call to link a source, never an alarm.
//
// Pure/props-driven: states + counts arrive resolved; jump / link-source reach
// out via props. Reuses the Editor/Chat-neutral Grounding vocabulary verbatim.

export { GroundingLens };

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import { GroundingEvidence } from "../../Grounding/GroundingEvidence/GroundingEvidence.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { GroundingCoverage, GroundingElementRow } from "../types.js";
import "./Inspection.css";

type GroundingLensProps = {
    // The material whose grounding is inspected, resolved title. Optional so
    // the lens can render its own "pick a material" empty state.
    readonly materialTitle?: string;
    readonly elements: readonly GroundingElementRow[];
    readonly coverage: GroundingCoverage;
    // Jump to a source fragment behind a resolved anchor (per element).
    readonly onJump?: (elementId: string, anchorIndex: number) => void;
    // Link a source to an ungrounded element (the calm call to action).
    readonly onLinkSource?: (elementId: string) => void;
};

const GroundingLens = ({
    materialTitle,
    elements,
    coverage,
    onJump,
    onLinkSource,
}: GroundingLensProps): ReactNode => {
    if (elements.length === 0) {
        return (
            <EmptyState
                icon={<Icon name="anchor" size="lg" />}
                title={<Trans>Nothing to ground yet</Trans>}
                body={
                    <Trans>
                        Open a material to see, element by element, which
                        sources back it up.
                    </Trans>
                }
            />
        );
    }
    return (
        <div className="bilda-hi-grounding">
            {materialTitle !== undefined && (
                <Text as="p" size="sm" tone="dim">
                    <Trans>Grounding for {materialTitle}</Trans>
                </Text>
            )}
            <Coverage coverage={coverage} />
            <ul className="bilda-hi-grounding__list">
                {elements.map((element) => (
                    <li key={element.id} className="bilda-hi-grounding__row">
                        <div className="bilda-hi-grounding__label">
                            <Text as="span" size="sm" weight="medium">
                                {element.elementLabel}
                            </Text>
                            {element.concept !== undefined && (
                                <Text as="span" size="xs" tone="faint">
                                    {element.concept}
                                </Text>
                            )}
                        </div>
                        <GroundingEvidence
                            state={element.state}
                            onJump={
                                onJump
                                    ? (anchorIndex) =>
                                          onJump(element.id, anchorIndex)
                                    : undefined
                            }
                            onLinkSource={
                                onLinkSource
                                    ? () => onLinkSource(element.id)
                                    : undefined
                            }
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

// The glanceable roll-up: counts with their state glyph, never a percentage.
const Coverage = ({ coverage }: { coverage: GroundingCoverage }): ReactNode => (
    <p className="bilda-hi-grounding__coverage">
        <span className="bilda-hi-grounding__count">
            <Icon name="check" size="xs" />
            <Text as="span" size="sm">
                <Trans>{coverage.grounded} grounded</Trans>
            </Text>
        </span>
        <span className="bilda-hi-grounding__count">
            <Icon name="warning" size="xs" />
            <Text as="span" size="sm">
                <Trans>{coverage.weak} weak</Trans>
            </Text>
        </span>
        <span className="bilda-hi-grounding__count">
            <Icon name="unlink" size="xs" />
            <Text as="span" size="sm">
                <Trans>{coverage.ungrounded} unlinked</Trans>
            </Text>
        </span>
    </p>
);
